import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { PaperPlaneRight, Sparkle, X } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * QUILLA IA chat component.
 * Modo "gerencia" (chat completo) o "copilot" (modal contextual con report_id).
 */
export default function QuillaIA({ context = "gerencia", reportId = null, onClose = null }) {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [cost, setCost] = useState(0);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim()) return;
    const q = text.trim();
    setMessages(m => [...m, { role: "user", text: q }]);
    setText(""); setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", {
        session_id: sessionId, text: q, context, report_id: reportId
      });
      setSessionId(data.session_id);
      setCost(c => c + data.est_cost_usd);
      setMessages(m => [...m, { role: "assistant", text: data.text, cost: data.est_cost_usd }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "QUILLA IA no disponible");
      setMessages(m => [...m, { role: "assistant", text: "Lo siento, no pude procesar tu consulta. Intenta de nuevo.", error: true }]);
    } finally { setLoading(false); }
  };

  const quickPrompts = context === "gerencia" ? [
    "¿Qué me recomiendas priorizar esta semana?",
    "¿Cuánto llevamos gastado en la piscina este trimestre?",
    "¿Qué habitaciones tienen reportes repetidos?",
    "Genera un resumen de las últimas 24 horas.",
  ] : [
    "¿Por dónde empiezo el diagnóstico?",
    "¿Qué revisar antes de pedir repuesto?",
    "¿Historial de este activo?",
  ];

  return (
    <div className={context === "copilot" ? "fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" : ""} onClick={context === "copilot" ? onClose : undefined}>
      <div className={context === "copilot" ? "q-card w-full max-w-2xl max-h-[85vh] flex flex-col" : "q-card flex flex-col h-[calc(100vh-260px)] min-h-[500px]"}
           onClick={(e) => e.stopPropagation()} data-testid="quilla-ia-chat">
        <div className="flex items-center gap-3 p-4 border-b border-[color:var(--q-border)]">
          <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{background: "#1F3864", color: "#C9A227"}}>
            <Sparkle size={18} weight="fill"/>
          </div>
          <div className="flex-1">
            <div className="font-semibold">QUILLA IA</div>
            <div className="text-xs text-[color:var(--q-muted)]">
              {context === "copilot" ? "Copiloto técnico · contexto de la orden" : "Asistente ejecutivo · Claude Sonnet 4.5"}
            </div>
          </div>
          <div className="text-xs text-[color:var(--q-muted)] font-mono">≈US${cost.toFixed(4)}</div>
          {onClose && <button data-testid="close-ia" onClick={onClose} className="q-btn-ghost !p-2"><X size={16}/></button>}
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && (
            <div className="text-center text-sm text-[color:var(--q-muted)] py-6">
              <Sparkle size={32} weight="fill" className="mx-auto mb-3 text-[color:var(--q-gold)]"/>
              <div>Preguntame lo que quieras del hotel. Conozco tus reportes, activos, proveedores y estructura.</div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[color:var(--q-navy)] text-white" : "bg-neutral-100 text-[color:var(--q-ink)]"} ${m.error ? "!bg-red-50 !text-red-800" : ""}`}>
                {m.text}
                {m.cost && <div className="text-[9px] mt-2 opacity-60 font-mono">≈US${m.cost.toFixed(4)}</div>}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start"><div className="bg-neutral-100 rounded-2xl px-4 py-3 text-sm q-pulse">Pensando…</div></div>
          )}
          <div ref={endRef}/>
        </div>

        {messages.length === 0 && (
          <div className="px-4 pb-2 flex gap-2 flex-wrap">
            {quickPrompts.map(p => (
              <button key={p} onClick={() => setText(p)} className="text-xs bg-neutral-100 hover:bg-neutral-200 px-3 py-1.5 rounded-full">
                {p}
              </button>
            ))}
          </div>
        )}

        <div className="p-3 border-t border-[color:var(--q-border)] flex gap-2">
          <input
            data-testid="ia-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
            placeholder="Escribe tu pregunta…"
            className="flex-1 h-11 px-3 rounded-lg border border-[color:var(--q-border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--q-navy)]"
          />
          <button data-testid="ia-send" onClick={send} disabled={loading || !text.trim()} className="q-btn-primary !px-4 disabled:opacity-40">
            <PaperPlaneRight size={18} weight="fill"/>
          </button>
        </div>
      </div>
    </div>
  );
}
